import { Injectable, inject, signal, computed } from '@angular/core';
import { MockDataService } from './mock-data.service';
import { AuthService } from './auth.service';
import { DonationSession } from '../models/domain.models';
import { ApiDonorResponse } from '../../modules/donation-portal/features/donors/models/donor-management.response';
import { UserType, UserOrigin } from '../models/auth.models';
import { environment } from '../../../environments/environment';

export type PortalContext = 'donation' | 'donor';

@Injectable({ providedIn: 'root' })
export class CurrentUserService {
  private readonly mockData = inject(MockDataService);
  private readonly authService = inject(AuthService);

  private readonly _portal = signal<PortalContext>(this.resolvePortalFromToken());
  readonly portal = this._portal.asReadonly();

  private readonly _donorId = signal<number>(this.resolveDonorIdFromToken());
  readonly donorId = this._donorId.asReadonly();

  readonly isAuthenticated = computed(() => this.authService.isAuthenticated());

  /** Company ID sourced from the JWT `companyid` claim. */
  readonly companyId = computed(() => Number(this.authService.getUserInfo()?.companyId ?? '0'));

  /** Store ID for the current session — from mock session data until a User service resolves it from the API. */
  readonly storeId = computed(() => this.mockData.session.locationId);

  readonly donor = computed<ApiDonorResponse>(() => {
    const id = this._donorId();
    return this.mockData.donors.find(d => d.id === id) ?? this.mockData.donors[0];
  });

  readonly donationSession = computed<DonationSession>(() => this.mockData.session);

  /**
   * Display name — portal-aware.
   * Donation portal: always shows session user name.
   * Donor portal: always shows donor name.
   */
  readonly displayName = computed(() => {
    if (this._portal() === 'donor') {
      const d = this.donor();
      return d.firstName ? `${d.firstName} ${d.lastName ?? ''}`.trim() : d.referenceNumber;
    }
    if (!environment.useMockData) {
      return this.authService.getUserInfo()?.fullName || this.authService.getUserInfo()?.email || 'User';
    }
    return this.mockData.session.staffName;
  });

  readonly email = computed(() => {
    if (this._portal() === 'donor') {
      return this.donor().email ?? '';
    }
    return this.authService.getUserInfo()?.email ?? '';
  });

  readonly initials = computed(() => {
    const name = this.displayName();
    const parts = name.split(' ').filter(Boolean);
    return parts.length >= 2
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
      : (parts[0]?.[0] ?? '').toUpperCase();
  });

  readonly userId = computed(() => {
    if (this._portal() === 'donor') return String(this._donorId());
    if (!environment.useMockData) return this.authService.getUserInfo()?.userId ?? '';
    return String(this.mockData.session.staffId);
  });

  readonly role = computed(() => this.authService.getUserInfo()?.roles[0] ?? '');

  /** All granted roles from the JWT `custom:role` claim. */
  readonly roles = computed<string[]>(() => this.authService.getUserInfo()?.roles ?? []);

  /** Full name directly from the JWT `fullname` claim. */
  readonly fullName = computed(() => this.authService.getUserInfo()?.fullName ?? '');

  /** Phone number from the JWT `phonenumber` claim. */
  readonly phoneNumber = computed(() => this.authService.getUserInfo()?.phoneNumber ?? '');

  /** Whether the user must change their password on next login. */
  readonly mustChangePasswordOnLogin = computed(() => {
    const val = this.authService.getUserInfo()?.mustChangePassword;
    return val?.toLowerCase() === 'true';
  });

  /** User type sourced from the JWT `custom:usertype` claim (e.g. "AppUser"). */
  readonly userType = computed<UserType>(() => {
    const val = this.authService.getUserInfo()?.userType;
    return val != null && val in UserType ? UserType[val as keyof typeof UserType] : UserType.AppUser;
  });

  /** User origin sourced from the JWT `custom:userorigin` claim (e.g. "Internal"). */
  readonly userOrigin = computed<UserOrigin>(() => {
    const val = this.authService.getUserInfo()?.userOrigin;
    return val != null && val in UserOrigin ? UserOrigin[val as keyof typeof UserOrigin] : UserOrigin.Unknown;
  });

  /** IANA/Windows timezone code from the JWT `custom:timezonecode` claim. Defaults to UTC. */
  readonly timezoneCode = computed(() => this.authService.getUserInfo()?.timezoneCode ?? 'UTC');

  /**
   * @internal Called by DevAuthService and portal shell components only.
   * Validates that the caller has a matching JWT role before allowing the switch.
   */
  setPortal(portal: PortalContext): void {
    const jwtRoles = this.authService.getUserInfo()?.roles ?? [];
    // Map portal context back to JWT role for validation
    const expectedJwtRole = portal === 'donation' ? 'staff' : portal;
    if (jwtRoles.length > 0 && !jwtRoles.includes(expectedJwtRole)) {
      if (!environment.production) {
        console.warn(`setPortal('${portal}') rejected — JWT roles are '${jwtRoles.join(', ')}'`);
      }
      return;
    }
    this._portal.set(portal);
  }

  /**
   * @internal Called by DevAuthService only.
   * Validates that the ID matches the JWT userid claim.
   */
  setDonorId(id: number): void {
    const jwtUserId = this.authService.getUserInfo()?.userId;
    if (jwtUserId && Number(jwtUserId) !== id) {
      if (!environment.production) {
        console.warn(`setDonorId(${id}) rejected — JWT userid is '${jwtUserId}'`);
      }
      return;
    }
    this._donorId.set(id);
  }

  /**
   * Resolve portal context from JWT role on service init / page refresh.
   */
  private resolvePortalFromToken(): PortalContext {
    const info = this.authService.getUserInfo();
    return info?.roles.includes('donor') ? 'donor' : 'donation';
  }

  /**
   * Resolve donor ID from JWT on service init / page refresh.
   */
  private resolveDonorIdFromToken(): number {
    const info = this.authService.getUserInfo();
    if (info?.roles.includes('donor') && info.userId) {
      const id = Number(info.userId);
      if (!isNaN(id) && this.mockData.donors.some(d => d.id === id)) {
        return id;
      }
    }
    return this.mockData.donors[0]?.id ?? 0;
  }
}
